import {
  notifyCreditExhaustion,
} from "@tribunalea/ui/credit-exhaustion";

const API_URL = import.meta.env?.VITE_API_URL || "http://localhost:4000";

function fileNameFromDisposition(value, fallback) {
  const encoded = value?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (!encoded) return fallback;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return fallback;
  }
}

export function createApi(tokenGetter) {
  const token = async (options) => tokenGetter ? tokenGetter(options) : null;
  const request = async (path, options = {}) => {
    const accessToken = await token();
    const headers = {
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...options.headers,
    };
    if (options.body && !(options.body instanceof FormData)) {
      headers["content-type"] = "application/json";
    }
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      credentials: "include",
      headers,
    });
    const payload = await response.json().catch(() => ({}));
    notifyCreditExhaustion(payload);
    if (!response.ok) {
      const error = new Error(
        payload.error || "No pudimos completar la operación.",
      );
      error.code = payload.code;
      error.issues = payload.issues;
      throw error;
    }
    return payload.data;
  };
  const download = async (path, fallback, open = false, page = null) => {
    const accessToken = await token();
    const response = await fetch(`${API_URL}${path}`, {
      credentials: "include",
      headers: accessToken
        ? { authorization: `Bearer ${accessToken}` }
        : {},
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      notifyCreditExhaustion(payload);
      throw new Error(payload.error || "No pudimos descargar el archivo.");
    }
    const blob = await response.blob();
    const fileName = fileNameFromDisposition(
      response.headers.get("content-disposition"),
      fallback,
    );
    const namedBlob = typeof File === "function"
      ? new File([blob], fileName, { type: blob.type })
      : blob;
    const url = URL.createObjectURL(namedBlob);
    if (open) {
      window.open(
        page ? `${url}#page=${page}` : url,
        "_blank",
        "noopener,noreferrer",
      );
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
  };
  const blob = async (path) => {
    const accessToken = await token();
    const response = await fetch(`${API_URL}${path}`, {
      credentials: "include",
      headers: accessToken
        ? { authorization: `Bearer ${accessToken}` }
        : {},
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      notifyCreditExhaustion(payload);
      throw new Error(payload.error || "No pudimos abrir el documento.");
    }
    return response.blob();
  };
  const openRemoteDocument = async (path, body, fallback) => {
    const accessToken = await token();
    const response = await fetch(`${API_URL}${path}`, {
      method: "POST",
      credentials: "include",
      headers: {
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const payload = await response.json().catch(() => ({}));
      notifyCreditExhaustion(payload);
      if (!response.ok) {
        throw new Error(
          payload.error || "No pudimos abrir el documento.",
        );
      }
      return payload.data;
    }
    if (!response.ok) {
      throw new Error("No pudimos abrir el documento.");
    }
    const documentBlob = await response.blob();
    const fileName = fileNameFromDisposition(
      response.headers.get("content-disposition"),
      fallback,
    );
    const namedBlob = typeof File === "function"
      ? new File([documentBlob], fileName, { type: documentBlob.type })
      : documentBlob;
    const url = URL.createObjectURL(namedBlob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { status: "opened" };
  };
  const requestExpedienteDocument = async (input) => {
    const accessToken = await token();
    const response = await fetch(
      `${API_URL}/api/instrumenta/expedientes/view/actuaciones/document`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(input),
      },
    );
    const contentType = response.headers.get("content-type") || "";
    if (response.ok && contentType.includes("application/pdf")) {
      return {
        status: "pdf",
        blob: await response.blob(),
        fileName: fileNameFromDisposition(
          response.headers.get("content-disposition"),
          "actuacion.pdf",
        ),
      };
    }
    const payload = await response.json().catch(() => ({}));
    notifyCreditExhaustion(payload);
    if (!response.ok) {
      const error = new Error(
        payload.error || "No pudimos consultar el PDF.",
      );
      error.code = payload.code;
      throw error;
    }
    return payload.data;
  };

  return {
    lookupJudiciaryExpedientes: ({ query, limit = 10, page = 1 }) =>
      request(
        `/api/instrumenta/expedientes/lookup?q=${encodeURIComponent(query)}&limit=${limit}&page=${page}`,
      ),
    getExpedienteFromSource: (identifier) =>
      request(
        `/api/instrumenta/expedientes/source-view?identifier=${encodeURIComponent(identifier)}`,
      ),
    getExpediente: (cuij, { includeTimeline = false } = {}) =>
      request(
        `/api/instrumenta/expedientes/view?cuij=${encodeURIComponent(cuij)}&includeTimeline=${includeTimeline}`,
      ),
    getExpedienteTimeline: (cuij) =>
      request(
        `/api/instrumenta/expedientes/view/timeline?cuij=${encodeURIComponent(cuij)}`,
      ),
    actuacionDocument: ({ cuij, actId, attachmentId }) =>
      requestExpedienteDocument({
        cuij,
        actId,
        ...(attachmentId ? { attachmentId } : {}),
      }),
    limits: () => request("/api/instrumenta/limits"),
    listTemplates: () => request("/api/instrumenta/templates"),
    getTemplate: (id) => request(`/api/instrumenta/templates/${id}`),
    createTemplate: (form) =>
      request("/api/instrumenta/templates", { method: "POST", body: form }),
    createTemplateVersion: (id, form) =>
      request(`/api/instrumenta/templates/${id}/versions`, {
        method: "POST",
        body: form,
      }),
    archiveTemplate: (id) =>
      request(`/api/instrumenta/templates/${id}`, { method: "DELETE" }),
    importTemplate: (file) => {
      const form = new FormData();
      form.append("template", file);
      return request("/api/instrumenta/templates/import", {
        method: "POST",
        body: form,
      });
    },
    exportTemplate: (id) =>
      download(
        `/api/instrumenta/templates/${id}/export`,
        "plantilla.tribunalea.json",
      ),
    downloadTemplate: (id) =>
      download(`/api/instrumenta/templates/${id}/file`, "plantilla.xlsx"),
    listRuns: ({ page = 1, limit = 12, query = "", status = "all", archived = false } = {}) => {
      const parameters = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        status,
        archived: String(archived),
      });
      if (query) parameters.set("q", query);
      return request(`/api/instrumenta/runs?${parameters}`);
    },
    createRun: (form) =>
      request("/api/instrumenta/runs", { method: "POST", body: form }),
    getRun: (id) => request(`/api/instrumenta/runs/${id}`),
    deleteRun: (id) =>
      request(`/api/instrumenta/runs/${id}`, { method: "DELETE" }),
    reviewRun: (id, fields) =>
      request(`/api/instrumenta/runs/${id}/fields`, {
        method: "PATCH",
        body: JSON.stringify({ fields }),
      }),
    saveRunDraft: (id, fields) =>
      request(`/api/instrumenta/runs/${id}/draft`, {
        method: "PATCH",
        body: JSON.stringify({ fields }),
      }),
    generateRun: (id) =>
      request(`/api/instrumenta/runs/${id}/generate`, { method: "POST" }),
    markRunReviewed: (id) =>
      request(`/api/instrumenta/runs/${id}/review`, { method: "POST" }),
    approveRun: (id) =>
      request(`/api/instrumenta/runs/${id}/approve`, { method: "POST" }),
    downloadRunReport: (id) =>
      download(`/api/instrumenta/runs/${id}/report`, "reporte-procesamiento.pdf"),
    downloadOutput: (id) =>
      download(`/api/instrumenta/runs/${id}/output`, "resultado.xlsx"),
    openDocument: (runId, documentId, page = null) =>
      download(
        `/api/instrumenta/runs/${runId}/documents/${documentId}`,
        "documento.pdf",
        true,
        page,
      ),
    downloadDocument: (runId, documentId) =>
      download(
        `/api/instrumenta/runs/${runId}/documents/${documentId}`,
        "documento.pdf",
      ),
    getDocumentBlob: (runId, documentId) =>
      blob(`/api/instrumenta/runs/${runId}/documents/${documentId}`),
    searchTaxIdentities: (query, limit = 10) =>
      request(
        `/api/tax-identities/search?query=${encodeURIComponent(query)}&limit=${limit}`,
      ),
    getTaxIdentity: (taxId) =>
      request(`/api/tax-identities/${encodeURIComponent(taxId)}`),
    searchProfessionals: (query, {
      limit = 20,
      jurisdiction = "all",
    } = {}) => {
      const parameters = new URLSearchParams({
        query,
        limit: String(limit),
        jurisdiction,
      });
      return request(`/api/professionals/search?${parameters}`);
    },
    searchJudiciarySentences: ({ query, page = 1, limit = 10 }) => {
      const parameters = new URLSearchParams({
        q: query,
        page: String(page),
        limit: String(limit),
      });
      return request(`/api/instrumenta/sentences/search?${parameters}`);
    },
    listJudiciarySentences: (cuij) =>
      request(
        `/api/instrumenta/sentences/actuations?cuij=${encodeURIComponent(cuij)}`,
      ),
    openJudiciarySentence: ({ cuij, actId, attachmentId }) =>
      openRemoteDocument(
        "/api/instrumenta/sentences/document",
        {
          cuij,
          actId,
          ...(attachmentId ? { attachmentId } : {}),
        },
        `sentencia-${cuij}.pdf`,
      ),
    getJurisprudenceCoverage: () =>
      request("/api/instrumenta/jurisprudence/meta"),
    searchJurisprudence: ({
      query,
      fromDate = "",
      toDate = "",
      tribunal = "all",
      limit = 12,
    }) => {
      const parameters = new URLSearchParams({
        q: query,
        tribunal,
        limit: String(limit),
      });
      if (fromDate) parameters.set("from", fromDate);
      if (toDate) parameters.set("to", toDate);
      return request(`/api/instrumenta/jurisprudence/search?${parameters}`);
    },
    getJurisprudence: (slug) =>
      request(`/api/instrumenta/jurisprudence/${encodeURIComponent(slug)}`),
    getDoctrineCoverage: () =>
      request("/api/instrumenta/doctrine/meta"),
    searchDoctrine: ({ query = "", page = 1, limit = 12 } = {}) => {
      const parameters = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (query) parameters.set("q", query);
      return request(`/api/instrumenta/doctrine/search?${parameters}`);
    },
    getDoctrine: (slug, { page = 1, limit = 30 } = {}) => {
      const parameters = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      return request(
        `/api/instrumenta/doctrine/${encodeURIComponent(slug)}?${parameters}`,
      );
    },
    getDoctrineCover: (path) => blob(path),
    getDoctrinePdf: (path) => blob(path),
    getDoctrinePdfSource: async (path, { refresh = false } = {}) => {
      const accessToken = await token(
        refresh ? { skipCache: true } : undefined,
      );
      return {
        url: `${API_URL}${path}`,
        httpHeaders: accessToken
          ? { authorization: `Bearer ${accessToken}` }
          : {},
        withCredentials: true,
      };
    },
    getNormsMeta: () => request("/api/instrumenta/norms/meta"),
    searchNorms: ({
      query = "",
      jurisdiction = "all",
      type = "all",
      status = "all",
      asOfDate = "",
      page = 1,
      limit = 12,
      passageLimit = 10,
    } = {}) => {
      const parameters = new URLSearchParams({
        jurisdiction,
        type,
        status,
        page: String(page),
        limit: String(limit),
        passageLimit: String(passageLimit),
      });
      if (query) parameters.set("q", query);
      if (asOfDate) parameters.set("asOf", asOfDate);
      return request(`/api/instrumenta/norms/search?${parameters}`);
    },
    getNorm: (slug, {
      asOfDate = "",
      page = 1,
      limit = 50,
      unitSlug = "",
    } = {}) => {
      const parameters = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (asOfDate) parameters.set("asOf", asOfDate);
      if (unitSlug) parameters.set("unit", unitSlug);
      return request(
        `/api/instrumenta/norms/${encodeURIComponent(slug)}?${parameters}`,
      );
    },
    listJudicialInterestRates: () =>
      request("/api/instrumenta/interest/rates"),
    listJudicialInterestSentences: (cuij) =>
      request(
        `/api/instrumenta/interest/sentence-documents?cuij=${encodeURIComponent(cuij)}`,
      ),
    createManualJudicialInterestDraft: (draftInput) =>
      request("/api/instrumenta/interest/drafts/manual", {
        method: "POST",
        body: JSON.stringify({ draftInput }),
      }),
    createSentenceJudicialInterestDraft: (
      files,
      fallbackDate,
      documents = [],
    ) => {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      form.append("documents", JSON.stringify(documents));
      form.append("fallbackDate", fallbackDate);
      return request("/api/instrumenta/interest/drafts/sentences", {
        method: "POST",
        body: form,
      });
    },
    listJudicialInterestHistory: ({ page = 1, limit = 10, query = "", status = "all", archived = false } = {}) => {
      const parameters = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        status,
        archived: String(archived),
      });
      if (query) parameters.set("q", query);
      return request(`/api/instrumenta/interest/history?${parameters}`);
    },
    getJudicialInterestCalculation: (id) =>
      request(`/api/instrumenta/interest/history/${id}`),
    updateJudicialInterestDraft: (id, kind, draftInput) =>
      request(`/api/instrumenta/interest/history/${id}/draft`, {
        method: "PATCH",
        body: JSON.stringify({ kind, draftInput }),
      }),
    retryJudicialInterestAnalysis: (id, clarification) =>
      request(`/api/instrumenta/interest/history/${id}/retry-analysis`, {
        method: "POST",
        body: JSON.stringify(clarification ? { clarification } : {}),
      }),
    archiveJudicialInterestCalculation: (id) =>
      request(`/api/instrumenta/interest/history/${id}`, { method: "DELETE" }),
    reviewJudicialInterestCalculation: (id) =>
      request(`/api/instrumenta/interest/history/${id}/review`, { method: "POST" }),
    approveJudicialInterestCalculation: (id) =>
      request(`/api/instrumenta/interest/history/${id}/approve`, { method: "POST" }),
    downloadJudicialInterestReport: (id) =>
      download(`/api/instrumenta/interest/history/${id}/report`, "reporte-intereses.pdf"),
    calculateJudicialInterest: (input) =>
      request("/api/instrumenta/interest/calculate", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    recalculateJudicialInterest: (id, input) =>
      request(`/api/instrumenta/interest/history/${id}/recalculate`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    analyzeJudicialInterestSentences: (files, fallbackDate) => {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      form.append("fallbackDate", fallbackDate);
      return request("/api/instrumenta/interest/analyze-sentences", {
        method: "POST",
        body: form,
      });
    },
    calculateJudicialInterestBatch: (input) =>
      request("/api/instrumenta/interest/calculate-batch", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    recalculateJudicialInterestBatch: (id, input) =>
      request(`/api/instrumenta/interest/history/${id}/recalculate-batch`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    listNotebooks: () =>
      request("/api/instrumenta/library/notebooks"),
    getNotebook: (id) =>
      request(`/api/instrumenta/library/notebooks/${id}`),
    createNotebook: (input) =>
      request("/api/instrumenta/library/notebooks", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    updateNotebook: (id, input) =>
      request(`/api/instrumenta/library/notebooks/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    archiveNotebook: (id) =>
      request(`/api/instrumenta/library/notebooks/${id}`, {
        method: "DELETE",
      }),
    uploadNotebookSource: (id, file) => {
      const form = new FormData();
      form.append("file", file);
      return request(`/api/instrumenta/library/notebooks/${id}/sources`, {
        method: "POST",
        body: form,
      });
    },
    retryNotebookSource: (notebookId, sourceId) =>
      request(
        `/api/instrumenta/library/notebooks/${notebookId}/sources/${sourceId}/retry`,
        { method: "POST" },
      ),
    updateNotebookSource: (notebookId, sourceId, input) =>
      request(
        `/api/instrumenta/library/notebooks/${notebookId}/sources/${sourceId}`,
        { method: "PATCH", body: JSON.stringify(input) },
      ),
    deleteNotebookSource: (notebookId, sourceId) =>
      request(
        `/api/instrumenta/library/notebooks/${notebookId}/sources/${sourceId}`,
        { method: "DELETE" },
      ),
    openNotebookSource: (notebookId, sourceId, page = null) =>
      download(
        `/api/instrumenta/library/notebooks/${notebookId}/sources/${sourceId}/file`,
        "fuente.pdf",
        true,
        page,
      ),
    downloadNotebookSource: (notebookId, sourceId) =>
      download(
        `/api/instrumenta/library/notebooks/${notebookId}/sources/${sourceId}/file`,
        "fuente.pdf",
      ),
    getNotebookSourceBlob: (notebookId, sourceId) =>
      blob(
        `/api/instrumenta/library/notebooks/${notebookId}/sources/${sourceId}/file`,
      ),
  };
}
